/**
 * In-Law Birthdays — the dashboard detail page for the
 * /report-management/in-law-birthdays report.
 *
 * Laid out the way Birthdays this Month is: a month heading, then one row per
 * birthday under it —
 *
 *     day (Weekday) | Name* | Head of Line • Colour Line
 *
 * — with the count beside the heading and the marker key in the footer. There
 * is no age column: in-laws never carry an age on the dashboard lists, so the
 * column Birthdays prints for descendants has nothing to hold here.
 *
 * The page opens on the current month of the current year. The Month and Year
 * pickers at the head of the filter bar re-fetch the report: month narrows the
 * list to one month (or shows the whole year, month by month), and year only
 * moves the weekday each birthday falls on, since a birthday comes round every
 * year. They are the shared DashboardPeriodPickers — the same pair Birthdays
 * this Month leads its bar with — styled as the sort buttons are, so the bar
 * reads as one row of controls that scrolls sideways on a phone.
 *
 * The rows are the report endpoint's own — living, still-married in-laws with
 * a birth date, already grouped by month and read day-ascending — so the count
 * here is the number the dashboard card shows. /dashboard-statistics-with-details
 * is read for what the report does not name: who heads each family line (for
 * the line column and the phone footer key), and the photos and multiple-birth
 * tags, which come off each in-law's roster record.
 *
 * The filter bar carries the sorts that apply to a list of in-laws — Date, A–Z,
 * Z–A, Line Color — and no member tabs, since every row is a living in-law.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardPeriodPickers, {
  DASHBOARD_PERIOD_ALL_MONTHS as ALL_MONTHS,
} from "./DashboardPeriodPickers";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import {
  DashboardInLawAsterisk,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  DashboardPhotoModal,
  DashboardPhotoName,
  getDashboardMemberNamePhotoUrls,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import { fetchInLawBirthdays } from "../../../Utils/dashboardInLawBirthdays";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";
import { getDashboardMemberNameParts } from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

/** Date is this page's own sort, the way it is on Birthdays this Month. */
const SORT_DATE_ASC = "date-asc";
const SORT_DATE_DESC = "date-desc";
const DEFAULT_SORT = SORT_DATE_ASC;

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Compact layout shows two-letter weekdays: Sunday -> SU, Monday -> MO, ... */
const abbreviateWeekday = (weekday) =>
  String(weekday || "")
    .slice(0, 2)
    .toUpperCase();

/** The weekday a month/day falls on in the year the report is read against. */
function getWeekdayName(year, monthIndex, day) {
  if (!Number.isFinite(year) || monthIndex < 0 || !(day > 0)) return "";
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (isNaN(date.getTime())) return "";
  return WEEKDAY_NAMES[date.getUTCDay()] || "";
}

/** Colour + hex, normalised the way the legend builder normalises them. */
const familyLineKey = (colorName, hexCode) =>
  `${String(colorName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+line$/i, "")}|${String(hexCode || "").toLowerCase()}`;

/** Unknown days sort last whichever way the column is read. */
const compareDays = (a, b) => {
  if (a === b) return 0;
  if (a === -1) return 1;
  if (b === -1) return -1;
  return a - b;
};

const nameOptions = { unnamedLabel: "", appendDescendantSpouse: false };

const SortDirectionIcon = ({ direction }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginLeft: "4px" }}
    aria-hidden="true"
  >
    <path d={direction === "asc" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
  </svg>
);

function InLawBirthdays() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } =
    useDashboardPhotoModal();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  // 1–12, or ALL_MONTHS for the whole year.
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({ grandTotal: 0, months: [], rows: [] });
  // The family lines the footer key and the head-of-line names are built from.
  const [legendLines, setLegendLines] = useState([]);
  // The roster by member id: the report endpoint sends no photos and no
  // multiple-birth fields, so both are read off the in-law's roster record.
  const [rosterById, setRosterById] = useState(() => new Map());
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Re-fetched whenever the pickers change; a response for a selection the
  // user has already moved on from is dropped.
  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      try {
        setLoading(true);
        const result = await fetchInLawBirthdays(getFamilyTreeIdFromStorage(), {
          year,
          month: month === ALL_MONTHS ? undefined : month,
        });
        if (!cancelled) setReport(result);
      } catch (error) {
        console.error("Error fetching in-law birthdays:", error);
        if (!cancelled) setReport({ grandTotal: 0, months: [], rows: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [year, month]);

  // Settled on its own: a call that failed leaves the report on screen with
  // only the head-of-line names, photos and birth tags missing.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode !== 200 || !response?.data) return;

        const members = extractAllMembersFromStatisticsResponse(response.data);
        setRosterById(
          new Map(
            members
              .map((member) => [String(member?.id ?? member?.member_id ?? ""), member])
              .filter(([id]) => id)
          )
        );
        setLegendLines(
          buildDashboardColorLegendLines(
            members,
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          )
        );
      } catch (error) {
        console.error("Error fetching family roster:", error);
      }
    };

    fetchRoster();
  }, []);

  /** The roster record behind a row where the roster knows it, else the row. */
  const getMarkerMember = useCallback(
    (row) => rosterById.get(row.id) ?? row.member,
    [rosterById]
  );

  /**
   * Head of line per colour, from the lines the footer key is built from — so
   * the name printed beside a colour on a row is spelled exactly as that key
   * spells it. Keyed on colour + hex, with a colour-only fallback.
   */
  const headNameByLine = useMemo(() => {
    const byColorAndHex = new Map();
    const byColor = new Map();

    legendLines.forEach(({ headName, colorName, hexCode }) => {
      if (!headName) return;
      byColorAndHex.set(familyLineKey(colorName, hexCode), headName);
      const colorOnly = familyLineKey(colorName, "");
      if (!byColor.has(colorOnly)) byColor.set(colorOnly, headName);
    });

    return { byColorAndHex, byColor };
  }, [legendLines]);

  const getLineHeadName = (colorName, hexCode) =>
    headNameByLine.byColorAndHex.get(familyLineKey(colorName, hexCode)) ||
    headNameByLine.byColor.get(familyLineKey(colorName, "")) ||
    "";

  const getRowName = useCallback(
    (row) => getDashboardMemberNameParts(row.member, nameOptions),
    []
  );

  /**
   * The report's months with the search box applied: each month keeps only
   * the rows whose name matches, on the name as printed. Blank search, whole
   * report.
   */
  const searchedMonths = useMemo(
    () =>
      report.months.map((group) => ({
        ...group,
        rows: filterDashboardMembersBySearch(
          group.rows,
          searchQuery,
          (row) => {
            const { baseName, descendantSpouseName } = getRowName(row);
            return `${baseName} ${descendantSpouseName}`.trim();
          },
          (row) => row.member
        ),
      })),
    [report.months, searchQuery, getRowName]
  );

  const isSearching = searchQuery.trim().length > 0;

  /**
   * The months on screen, each with its rows in the order the sort reads.
   * The month grouping is the report's own shape and stands under every sort;
   * only the rows inside a month re-order. Date descending also reads the
   * months last to first, so a whole-year list runs December back to January.
   */
  const sortedMonths = useMemo(() => {
    const sortName = (row) => {
      const { baseName, descendantSpouseName } = getRowName(row);
      return `${baseName} ${descendantSpouseName}`.trim().toLowerCase();
    };
    const byDayThenName = (a, b) =>
      compareDays(a.day, b.day) || sortName(a).localeCompare(sortName(b));

    const sortRows = (rows) => {
      const sorted = [...rows];
      switch (sortBy) {
        case "alpha-asc":
          return sorted.sort(
            (a, b) => sortName(a).localeCompare(sortName(b)) || compareDays(a.day, b.day)
          );
        case "alpha-desc":
          return sorted.sort(
            (a, b) => sortName(b).localeCompare(sortName(a)) || compareDays(a.day, b.day)
          );
        case "color":
          return sorted.sort(
            (a, b) =>
              a.colorName.toLowerCase().localeCompare(b.colorName.toLowerCase()) ||
              sortName(a).localeCompare(sortName(b)) ||
              compareDays(a.day, b.day)
          );
        case SORT_DATE_DESC:
          return sorted.sort(
            (a, b) => -compareDays(a.day, b.day) || sortName(a).localeCompare(sortName(b))
          );
        default:
          return sorted.sort(byDayThenName);
      }
    };

    const months = searchedMonths
      .filter((group) => group.rows.length > 0)
      .map((group) => ({ ...group, rows: sortRows(group.rows) }));

    return sortBy === SORT_DATE_DESC ? months.reverse() : months;
  }, [searchedMonths, sortBy, getRowName]);

  const visibleRows = useMemo(
    () => sortedMonths.flatMap((group) => group.rows),
    [sortedMonths]
  );

  const isDateSortActive = sortBy === SORT_DATE_ASC || sortBy === SORT_DATE_DESC;

  /** Two-state toggle, as on Birthdays this Month: clicking Date flips it. */
  const dateSortButton = (
    <button
      type="button"
      className={`dashboard-detail-sort-btn ${isDateSortActive ? "active" : ""}`}
      onClick={() =>
        setSortBy(sortBy === SORT_DATE_ASC ? SORT_DATE_DESC : SORT_DATE_ASC)
      }
      title="Sort by Date"
    >
      Date
      {isDateSortActive ? (
        <SortDirectionIcon direction={sortBy === SORT_DATE_ASC ? "asc" : "desc"} />
      ) : null}
    </button>
  );

  // The period pickers lead the filter bar, ahead of the sorts, so on a phone
  // they scroll sideways with the rest of the bar instead of taking a line of
  // their own. They stay up while a month is empty or re-fetching — the bar is
  // how the reader moves to another month.
  const periodPickers = (
    <DashboardPeriodPickers
      month={month}
      onMonthChange={setMonth}
      year={year}
      onYearChange={setYear}
    />
  );

  const renderRow = (row) => {
    const { baseName, descendantSpouseName } = getRowName(row);
    const markerMember = getMarkerMember(row);
    const showInLawAsterisk = !isExplicitDashboardStartOrTopOfLineMember(markerMember);
    const multipleBirthSup = getMultipleBirthSuperscriptText(markerMember);
    const weekday = getWeekdayName(year, row.monthIndex, row.day);

    return (
      <React.Fragment key={row.id}>
        <div className="in-law-birthdays-cell in-law-birthdays-date dashboard-detail-date-col">
          <span className="dashboard-detail-date-day">
            {row.day === -1 ? "" : row.day}
          </span>
          {weekday ? (
            <span className="dashboard-detail-date-weekday">
              ({isCompactLayout ? abbreviateWeekday(weekday) : weekday})
            </span>
          ) : null}
        </div>
        <div className="in-law-birthdays-cell in-law-birthdays-name">
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(markerMember)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={baseName}
                descendantSpouseName={descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && descendantSpouseName ? (
                    <DashboardInLawAsterisk />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !descendantSpouseName && (
              <DashboardInLawAsterisk />
            )}
            {multipleBirthSup && (
              <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
            )}
          </DashboardNameMarkerGroup>
        </div>
        {/* The wide layout names who heads the line on the row; the phone
            prints the colour alone and leaves the heads to the footer key. */}
        <div className="in-law-birthdays-cell in-law-birthdays-line">
          <DashboardLineColorName
            colorName={row.colorName}
            hexCode={row.hexCode}
            headName={
              isCompactLayout ? "" : getLineHeadName(row.colorName, row.hexCode)
            }
          />
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className="dashboard-container in-law-birthdays-page dashboard-detail-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">In-Law Birthdays</h3>
            {/* The count is the rows on screen: the month's own count, or the
                report's grand total when the whole year is showing. */}
            <div className="dashboard-detail-count">{visibleRows.length}</div>
            {!loading && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
              />
            )}
            <DashboardDetailSortFilterBar
              sortBy={sortBy}
              onSortChange={setSortBy}
              showMemberFilters={false}
              showAgeSorts={false}
              leadingSortButtons={
                <>
                  {periodPickers}
                  {dateSortButton}
                </>
              }
            />
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading in-law birthdays...</p>
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} noun="in-law birthdays" />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="in-law-birthdays-page dashboard-detail-content-wrapper">
            <div className="in-law-birthdays-list dashboard-detail-list">
              {sortedMonths.map((group) => (
                <React.Fragment key={group.month ?? group.monthName}>
                  <div className="in-law-birthdays-month-row">
                    <span className="dashboard-detail-month-name dashboard-section-heading">
                      {group.monthName}
                      {/* With All Months picked each heading carries its own count; a single month already shows it at the top. */}
                      {month === ALL_MONTHS && (
                        <span className="dashboard-section-heading-count">{` (${group.rows.length})`}</span>
                      )}
                    </span>
                  </div>
                  {group.rows.map(renderRow)}
                </React.Fragment>
              ))}
            </div>

            <div className="in-law-birthdays-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  visibleRows.map(getMarkerMember)
                )}
              />
              {isCompactLayout && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>

      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default InLawBirthdays;
